import React, { useState, useRef, useEffect, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { FilterDefault } from '@helpers/icon-dictionary';
import { getContextCoords } from '@helpers/utils';
import OutsideClickHandler from '@helpers/hoc/close/outside-click-handler';

import ContextModal, { contextModalWidth } from '@components/context-modal';
import AccuracyProgressToggle from '@components/accuracy-progress-toggle/accuracy-progress-toggle';
import { SELECT_ACCURACY_SUB_FILTER, SELECT_PROGRESS_SUB_FILTER } from '@containers/upf-assign/upf-assign-action-types';
import { clearCellSelection } from '@containers/upf-assign/upf-assign-actions';
import getTranslations from '@components/assignment-filters/translations';
import SvgIcon from '@components/svg-icon/svg-icon';
import { useTracking } from '../../analytics';

import { ACCURACY_SUB_FILTER, PROGRESS_SUB_FILTER, HIGHEST, ASSIGNED, VOLUNTARY } from './filters-constants';
import { ACCURACY } from '../accuracy-progress-toggle/accuracy-progress-constants';
import LocalStorage from '../../helpers/local-storage';

import { FilterLabel, AssignmentFiltersWrapper, FilerRow, IconWrapper } from './styled';

import FilterOptions from './filter-options/filter-options';

const AssignmentFilters = ({ i18n }) => {
  /**
   * STORE
   */
  const dispatch = useDispatch();
  const currentFilterSelected = useSelector(state => state.UPFAssignReducer.accuracyProgressToggle.currentFilter);
  const { trackFilterApplied } = useTracking();

  /**
   * TRANSLATIONS
   */
  const { ASSIGNED_ATTEMPT_TEXT, VOLUNTARY_ATTEMPT_TEXT, HIGHEST_ATTEMPT_TEXT, FILTER_LABEL_HOVER_TEXT } =
    getTranslations({ i18n });

  /**
   * REFS
   */
  const filterMenuRef = useRef(null);

  /**
   * LOCAL STORAGE
   */
  const accuracySubFilterSelected = LocalStorage.getItem(ACCURACY_SUB_FILTER) || ASSIGNED;
  const progressSubFilterSelected = LocalStorage.getItem(PROGRESS_SUB_FILTER) || ASSIGNED;

  /**
   * STATE
   */
  const [accuracySubFilter, setAccuracySubFilter] = useState(accuracySubFilterSelected);
  const [progressSubFilter, setProgressSubFilter] = useState(progressSubFilterSelected);
  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);
  const [positionArrow, setPositionArrow] = useState(0);
  const [showContextMenu, setContextMenu] = useState(false);

  /**
   * VARIABLES
   */
  const isAccuracySelected = currentFilterSelected === ACCURACY;
  const handleSelection = filter => {
    if (isAccuracySelected) {
      setAccuracySubFilter(filter);
    } else {
      setProgressSubFilter(filter);
    }

    trackFilterApplied({ filterType: currentFilterSelected, subFilter: filter });
  };
  const subFilterText = isAccuracySelected ? accuracySubFilter : progressSubFilter;

  /**
   * FUNCTIONS
   */
  const showContextModal = () => {
    const { posX, posY, posArrow } = getContextCoords(filterMenuRef.current.getBoundingClientRect(), contextModalWidth);
    setPositionY(posY);
    setPositionX(posX - contextModalWidth / 2.5);
    setPositionArrow(posArrow);
    setContextMenu(!showContextMenu);
  };

  const getFilterText = filter => {
    switch (filter) {
      case HIGHEST:
        return HIGHEST_ATTEMPT_TEXT;
      case ASSIGNED:
        return ASSIGNED_ATTEMPT_TEXT;
      case VOLUNTARY:
        return VOLUNTARY_ATTEMPT_TEXT;
      default:
        return '';
    }
  };

  /**
   * EFFECTS
   */
  useEffect(() => {
    LocalStorage.setItem(ACCURACY_SUB_FILTER, accuracySubFilter);
    dispatch({ type: SELECT_ACCURACY_SUB_FILTER, accuracySubFilter });
    dispatch(clearCellSelection());
  }, [accuracySubFilter]);

  useEffect(() => {
    LocalStorage.setItem(PROGRESS_SUB_FILTER, progressSubFilter);
    dispatch({ type: SELECT_PROGRESS_SUB_FILTER, progressSubFilter });
    dispatch(clearCellSelection());
  }, [progressSubFilter]);

  return (
    <Fragment>
      <AccuracyProgressToggle i18n={i18n} />
      <AssignmentFiltersWrapper ref={filterMenuRef}>
        <OutsideClickHandler callback={() => setContextMenu(false)} forButton="assignment-filter">
          <ContextModal
            borderRadius={1}
            color="#3b4e5e"
            show={showContextMenu}
            positionArrow={positionArrow}
            positionX={positionX}
            positionY={positionY}
            overflow="visible"
          >
            <FilterOptions filter={subFilterText} handleSelection={handleSelection} i18n={i18n} />
          </ContextModal>
        </OutsideClickHandler>

        <FilerRow onClick={showContextModal} title={FILTER_LABEL_HOVER_TEXT}>
          <IconWrapper>
            <SvgIcon icon={FilterDefault} />
          </IconWrapper>

          <FilterLabel data-testid="sub-filter-label">{getFilterText(subFilterText)}</FilterLabel>
        </FilerRow>
      </AssignmentFiltersWrapper>
    </Fragment>
  );
};

AssignmentFilters.propTypes = {
  i18n: PropTypes.object
};

export default AssignmentFilters;
